import React, { useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import { Button, Card, CardContent, Grid } from '@mui/material'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { isNil, orderBy, uniq, uniqBy } from 'lodash'
import moment from 'moment'
import useListIndexed from 'utils/customHook/useListIndexed'
import { FILTER_TYPE } from 'administration/components/user/constants/UserConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { SEUILS_LIM, SEUILS_REF } from 'quality/components/AEPoverview/AEPOverview'
import Input from 'components/forms/Input'
import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import SimpleCheckbox from 'components/forms/SimpleCheckbox'
import PropTypes from 'prop-types'
import Select from 'components/forms/Select'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import SupportAction from 'referencial/components/support/actions/SupportAction'
import ThresholdSelect from 'components/forms/specific/ThresholdSelect'

const DistributionUnitsConformityFilter = ({
    stationType,
    data = [],

    defaultSearchValue,
    defaultFilter,
    defaultFilterResults,
    defaultSector,
    defaultCity,
    conformityDefaultFilter = {},
    defaultOnlyWithData = false,
    criterias = {},
    loading = false,

    onValidate = () => { },
    onReset = () => { },
}) => {
    const {
        cities,
        distributionUnits,
        distributionUnitSectors,
        producers,
        supports,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
        distributionUnitSectors: store.DistributionUnitReducer.distributionUnitSectors,
        producers: store.ContributorReducer.producers,
        supports: store.SupportReducer.supports,
    }), shallowEqual)

    const dispatch = useDispatch()

    const indexedProducers = useListIndexed(producers, 'id')
    const indexedSupports = useListIndexed(supports, 'id')
    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const [search, setSearch] = useState(defaultSearchValue)
    const [tmpFilter, setTmpFilter] = useState(defaultFilter || FILTER_TYPE.NOFILTER)
    const [tmpFilterResults, setTmpFilterResults] = useState(defaultFilterResults || [])
    const [tmpSector, setTmpSector] = useState(defaultSector)
    const [tmpCity, setTmpCity] = useState(defaultCity)

    const [producer, setProducer] = useState(conformityDefaultFilter.producer)
    const [year, setYear] = useState(conformityDefaultFilter.year)
    const [referenceThreshold, setReferenceThreshold] = useState(conformityDefaultFilter.referenceThreshold)
    const [limitThreshold, setLimitThreshold] = useState(conformityDefaultFilter.limitThreshold)
    const [support, setSupport] = useState(conformityDefaultFilter.support)
    const [onlyWithData, setOnlyWithData] = useState(defaultOnlyWithData)

    useEffect(() => {
        if (!supports.length) {
            dispatch(SupportAction.fetchSupports())
        }
    }, [])

    const onSearch = (newSearch) => onValidate({
        searchValue: newSearch ?? search,
        filter: tmpFilter,
        filterResults: tmpFilterResults,
        sector: tmpSector,
        city: tmpCity,
        onlyWithData,
        conformityFilter: {
            producer,
            year,
            referenceThreshold,
            limitThreshold,
            support,
        },
    })

    const filteredTowns = distributionUnits?.flatMap(d => d.geographicSector?.split(','))

    // listes limitées aux valeurs présentes dans les opérations des UDI (calculées côté back)
    // l'année en cours (année par défaut) est toujours proposée, même sans opération
    const yearList = useMemo(() => orderBy(uniq([moment().year(), ...(criterias.years || [])]), y => y, 'desc').map(y => ({ code: y, name: y })), [criterias.years])

    const producerList = useMemo(() => (criterias.producers || []).map(id => {
        const prod = indexedProducers[id]
        if (isNil(prod)) return prod
        return { id, name: `${prod.mnemonique || prod.name || ''} ${showSandreCode ? `[${prod.siret || prod.code}]` : ''}` }
    }).filter(p => !!p), [criterias.producers, indexedProducers, showSandreCode])

    const supportList = useMemo(() => (criterias.supports || []).map(id => indexedSupports[id]).filter(s => !!s), [criterias.supports, indexedSupports])

    return (
        <Card>
            <CardContent>
                <Grid container spacing={1} alignItems='center'>
                    <Grid size={3}>
                        <Input
                            tooltip={i18n.searchThenEnter}
                            title={i18n.search}
                            value={search}
                            onChange={setSearch}
                            onEnterKeyPress={(value) => {
                                setSearch(value)
                                // pas de nouvelle recherche tant que les bilans en cours ne sont pas tous reçus
                                if (!loading) {
                                    onSearch(value)
                                }
                            }}
                            clearFunction
                            data-cy='search'
                        />
                    </Grid>
                    <Grid size={3}>
                        <SimpleFilterSelect
                            stationType={stationType}
                            onChange={(filterStations, newFilter) => {
                                setTmpFilterResults(filterStations)
                                setTmpFilter(newFilter)
                            }}
                            nullLabel={i18n.allStations}
                            stations={data}
                        />
                    </Grid>
                    <Grid size={3}>
                        <SuperMultiAutocomplete
                            options={uniqBy(distributionUnitSectors, 'name')}
                            label={i18n.sector}
                            onChange={setTmpSector}
                            values={tmpSector}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            options={cities.filter(c => filteredTowns.includes(c.code))}
                            label={i18n.city}
                            onChange={setTmpCity}
                            value={tmpCity}
                            clearFunction
                            noNullValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            options={producerList}
                            label={i18n.producers}
                            value={producer}
                            nullLabel='&nbsp;'
                            onChange={setProducer}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label={i18n.support}
                            options={supportList}
                            onChange={setSupport}
                            value={support}
                            keyValue={'id'}
                            displayWithCode={showSandreCode}
                        />
                    </Grid>
                    <Grid size={1}>
                        <Select
                            options={yearList}
                            label={i18n.year}
                            value={year}
                            onChange={setYear}
                            nullLabel='&nbsp;'
                            noSort
                        />
                    </Grid>
                    <Grid size={3}>
                        <ThresholdSelect
                            title={i18n.referenceThreshold}
                            selected={referenceThreshold}
                            onChange={setReferenceThreshold}
                            fieldRef={SEUILS_REF}
                        />
                    </Grid>
                    <Grid size={3}>
                        <ThresholdSelect
                            title={i18n.limitThreshold}
                            selected={limitThreshold}
                            onChange={setLimitThreshold}
                            fieldRef={SEUILS_LIM}
                        />
                    </Grid>
                    <Grid size='grow'>
                        <SimpleCheckbox
                            label={i18n.onlyUnitsWithData}
                            checked={onlyWithData}
                            onToggle={() => setOnlyWithData(prev => !prev)}
                        />
                    </Grid>
                    <Grid container size='auto' justifyContent='flex-end' spacing={1}>
                        <Button
                            variant='outlined'
                            onClick={onReset}
                        >
                            {i18n.reinit}
                        </Button>
                        {/* grisé pendant le calcul : l'annulation se fait depuis la barre de progression */}
                        <Button
                            variant='contained'
                            onClick={() => onSearch()}
                            disabled={loading}
                            data-cy='searchBtn'
                        >
                            {i18n.search}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

DistributionUnitsConformityFilter.propTypes = {
    stationType: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({})),
    defaultSearchValue: PropTypes.string,
    defaultFilter: PropTypes.number,
    defaultFilterResults: PropTypes.arrayOf(PropTypes.number),
    defaultSector: PropTypes.number,
    defaultCity: PropTypes.number,
    conformityDefaultFilter: PropTypes.shape({
        producer: PropTypes.number,
        year: PropTypes.number,
        referenceThreshold: PropTypes.number,
        limitThreshold: PropTypes.number,
        support: PropTypes.string,
    }),
    defaultOnlyWithData: PropTypes.bool,
    loading: PropTypes.bool,
    criterias: PropTypes.shape({
        producers: PropTypes.arrayOf(PropTypes.number),
        supports: PropTypes.arrayOf(PropTypes.number),
        years: PropTypes.arrayOf(PropTypes.number),
    }),
    onValidate: PropTypes.func,
    onReset: PropTypes.func,
}

export default DistributionUnitsConformityFilter
